import { createFetch } from '@vueuse/core'
import { defineStore } from 'pinia'

import { Errors } from '@/enums/error'
import { resetSession } from '@/libs/auth'
import { markImagePreparing } from '@/libs/image'
import router from '@/router'
import { useAppStore } from '@/stores/app'
import type { HTTPError } from '@/types/Common'

export const useFetchStore = defineStore('fetch', () => {
    const appStore = useAppStore()

    const fetch = createFetch({
        baseUrl: '/api/startbox/v1',
        fetchOptions: {
            credentials: 'same-origin',
        },
        options: {
            // The one line that lets every caller say what actually went wrong. VueUse
            // resets `data` to `initialData` (null) the moment a response is not ok, which
            // threw away the plane's `{"error":{"code":8,"message":"no capacity"}}` body —
            // so `parseError(data.value, fallback)` never saw a code and every failed
            // action toasted the bland fallback ("An error occurred") instead of "the
            // server has no room for another workspace". With this on, `data` holds the
            // error envelope on a failure, and the existing per-call `parseError` toasts
            // turn specific. Nothing else changes: every success path is already gated on
            // the status code, and `unwrapList` reads an envelope as an empty list.
            updateDataOnError: true,
            beforeFetch({ options }) {
                // Cookie-based auth: the header is only sent when a token is actually stored.
                const token = appStore.getJWT()
                if (token) options.headers = { ...options.headers, Authorization: `Bearer ${token}` }
                return { options }
            },
            async onFetchError(ctx) {
                if (ctx.response?.status === 401 && appStore.isLoggedIn()) {
                    // The cached ensureSession() answer has to go too, or the route guard
                    // reads a stale "signed in" and bounces the user straight off /login.
                    appStore.setLogout()
                    resetSession()
                    await router.push({ name: 'login' })
                }

                // Error code 14 is the plane refusing because it is building the workspace
                // image. That is the gate closing under an open tab, and the gate takes the
                // whole app (invariant 27) — so the answer routes to the preparing page
                // rather than leaving a toast on a screen whose buttons all now refuse. It
                // is the second way in, beside the shell's own poll, and the faster one:
                // whoever pressed the button hears about it on the spot.
                if ((ctx.data as HTTPError | null)?.error?.code === Errors.ImagePreparing) {
                    markImagePreparing()

                    const current = router.currentRoute.value

                    if (current.name !== 'preparing') {
                        await router.push({ name: 'preparing', query: { redirect: current.fullPath } })
                    }
                }

                return ctx
            },
        },
    })

    return { fetch }
})
